import { useCallback, useEffect, useState } from "react";

import PropTypes from "prop-types";

import { OutletRoleEnum } from "../outlets/outlet-role";
import { apiClient, setActiveOutletId, getErrorMessage } from "../shared";
import AuthContext from "./auth-context";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [outlet, setOutlet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserPayload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiClient.get("users/me");
      setUser(data);
      return data;
    } catch (error) {
      setError(getErrorMessage(error, "Gagal memuat data pengguna"));
      console.error("Error fetching user payload:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const changeOutlet = useCallback(async (newOutlet) => {
    setOutlet(newOutlet);
    localStorage.setItem("outlet", JSON.stringify(newOutlet));
  }, []);

  const login = useCallback(
    async (credential) => {
      setIsLoading(true);
      setError(null);
      try {
        const {
          data: {
            data: { accessToken },
          },
        } = await apiClient.post("auth/sign-in", credential);
        localStorage.setItem("access_token", accessToken);
        const user = await fetchUserPayload();
        return user;
      } catch (error) {
        setError(getErrorMessage(error, "Gagal masuk"));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchUserPayload]
  );

  const logout = useCallback(async () => {
    setUser(null);
    setOutlet(null);
    setError(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("outlet");
  }, []);

  const fetchOutlet = useCallback(async () => {
    if (!user) return;

    const storedOutlet = localStorage.getItem("outlet");

    if (
      storedOutlet &&
      user.outlets?.some(({ id }) => id === JSON.parse(storedOutlet).id)
    ) {
      setOutlet(JSON.parse(storedOutlet));
    } else if (user && user.outlets.length > 0) {
      changeOutlet(user.outlets[0]);
    }
  }, [changeOutlet, user]);

  const hasPermission = useCallback(
    (permission) => {
      if (!user?.role?.permissions) return false;
      return user.role.permissions.some(({ action }) => action === permission);
    },
    [user]
  );

  // Computed values for outlet roles
  const isRetailOutlet = outlet?.role === OutletRoleEnum.RETAIL;
  const isHubOutlet = outlet?.role === OutletRoleEnum.HUB;

  // Auto-fetch outlet when user changes
  useEffect(() => {
    if (user) {
      fetchOutlet();
    }
  }, [user, fetchOutlet]);

  // Mirror the current outlet into the per-tab active Outlet-Id that
  // apiOutletClient reads at request time. Covers every path that changes the
  // outlet (switch, restore, logout) so requests never depend on another tab's
  // localStorage writes.
  useEffect(() => {
    setActiveOutletId(outlet?.id ?? null);
  }, [outlet]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        fetchUserPayload,
        fetchOutlet,
        user,
        outlet,
        changeOutlet,
        isLoading,
        error,
        hasPermission,
        isRetailOutlet,
        isHubOutlet,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
